import type { ErrorCode } from '@halvard/protocol';
import type { ice } from '@halvard/rules';
import type { IceBotLevel } from '@halvard/ai';
import type { SinkReason } from '@halvard/board3d';

/**
 * Every word the site shows, in English. `ko.ts` has the same shape; TypeScript keeps the two in
 * step. Sentences with values in them are functions, so each language can put the values where
 * its grammar wants them.
 */
export const en = {
  langName: 'English',
  meta: {
    description: 'Halvard: a Viking battle on a melting lake.',
  },
  nav: {
    skip: 'Skip to content',
    home: 'Halvard home',
    main: 'Main',
    play: 'Bot',
    online: 'Online',
    learn: 'Learn',
    language: 'Language',
  },
  titles: {
    playLocal: 'Play',
    playBot: 'Play the bot',
    botVsBot: 'Bot vs bot',
    playFriend: 'Play a friend',
    onlineMatch: 'Online match',
    learn: 'Learn',
    notFound: 'Off the trail',
  },
  home: {
    tagline: 'A Viking battle on a melting lake.',
    lead: 'Shove the enemy through the ice and take the three runestones before the thaw. Every lake is new, so there is no opening to learn by heart.',
    playBot: 'Play the bot',
    playOnline: 'Play a friend online',
    playLocal: 'Two players, one screen',
    learn: 'Learn the rules',
    picture:
      'A game in progress on the frozen lake: orange Brand warriors and teal Rime warriors on squares of ice, the shore and a longhouse behind.',
    factsTitle: 'How it plays',
    facts: [
      {
        image: '/shove.webp',
        title: 'Shove, don’t capture',
        body: 'Pick a warrior and every enemy in reach shows your strength against its shield wall: 3:2 works, grey fails. The faint warrior shows where the enemy will slide.',
        alt: 'Close-up: a Brand Huscarl lined up on a Rime Spear, a pink 3:2 badge above it and a faint copy of the Spear one square further on.',
      },
      {
        image: '/crack.webp',
        title: 'The ice is melting',
        body: 'Every three rounds, two squares crack. Red cracks open into holes three rounds later, taking whoever stands there.',
        alt: 'Close-up: a square of ice with a red crack across it.',
      },
      {
        image: '/runestone.webp',
        title: 'Hold the runestones',
        body: 'Each runestone is a whole 3×3 floe, outlined in the colour of the side that holds it. Brand needs 20 Glory from them in 16 rounds; Rime only has to hold.',
        alt: 'Close-up: a runestone floe outlined in teal, held by Rime.',
      },
    ],
  },
  notFound: {
    title: 'Off the trail',
    body: 'This page does not exist.',
    back: 'Back home',
  },

  sides: { brand: 'Brand', rime: 'Rime' } as Record<ice.Side, string>,
  warriors: { skier: 'Skier', spear: 'Spear', huscarl: 'Huscarl' } as Record<
    ice.WarriorType,
    string
  >,
  warriorName: (side: string, type: string) => `${side} ${type}`,
  /** Short rule under a warrior picked in the panel. */
  warriorRule: {
    skier: 'Force 1, 4 move points. Fast, but easy to shove.',
    spear: 'Force 2, 3 move points.',
    huscarl: 'Force 3, 2 move points. Slow and heavy.',
  } as Record<ice.WarriorType, string>,
  runestones: ['West runestone', 'Centre runestone', 'East runestone'],
  bots: {
    easy: { name: 'Easy', detail: 'Looks one move ahead and at your most dangerous shove.' },
    normal: { name: 'Normal', detail: 'Looks at its move and your reply.' },
    hard: { name: 'Hard', detail: 'Looks 2 moves ahead for each side.' },
    expert: { name: 'Expert', detail: 'Looks 3 moves ahead for each side. Slow.' },
  } as Record<IceBotLevel, { name: string; detail: string }>,

  game: {
    lakeArea: 'Lake',
    controls: 'Game controls',
    boardLabel: (seed: number, status: string) =>
      `Lake #${seed}. ${status}. Use the arrow keys to move, Enter to select.`,
    lake: (seed: number) => `Lake #${seed}`,
    round: (round: number, of: number) => `Round ${round} of ${of}`,
    hide: 'Hide',
    showControls: 'Controls',
    players: 'Players',
    you: 'You',
    opponent: 'Opponent',
    bot: (level: string) => `${level} bot`,
    sidePlayer: (side: string) => `${side} player`,
    toMove: (side: string) => `${side} to move`,
    places: (side: string) => `${side} places a warrior`,
    botTurn: (side: string, player: string, doing: 'thinking' | 'placing' | 'moving') =>
      `${side} (${player}) ${doing === 'thinking' ? 'is thinking…' : doing === 'placing' ? 'places a warrior' : 'to move'}`,
    glory: 'Glory',
    gloryMeter: 'Brand’s Glory',
    gloryLine: (glory: number, target: number) => `Brand ${glory} of ${target}`,
    gloryNeeds: (more: number, turns: number) =>
      `: needs ${more} more in ${turns} turn${turns === 1 ? '' : 's'}`,
    runestonesLabel: 'Runestones',
    holds: (side: string) => `${side} holds`,
    nobody: 'nobody',
    placeHeading: 'Place a warrior',
    placeGroup: 'Warrior to place',
    placeHint: (side: string, ranks: string) =>
      `Click a free square in ${side}’s territory (ranks ${ranks}).`,
    shoveTitle: (target: string) => `Shove ${target}`,
    shoveFrom: (from: string) => `from ${from}`,
    pointAnother: 'Point at another diamond to shove from there; click to play.',
    clickEnemy: 'Click the enemy to pick it, then choose where to shove from.',
    shoveFails: 'That shove fails: the shield wall holds. Try another square.',
    dotsHelp: 'Dots: where it can walk. Numbers over an enemy: your shove against its shield wall.',
    selectWarrior: 'Select one of your warriors.',
    pass: 'Pass (no move)',
    undo: 'Undo',
    cracks: 'Cracks',
    groansAt: (round: number) => `The ice groans at the end of round ${round}.`,
    noneYet: 'None yet.',
    /** A crack in the list, around the coloured word: [before, word, after]. */
    crack: (round: number, opensHole: boolean): [string, string, string] => [
      '',
      opensHole ? 'opens a hole' : 'thins',
      ` at the end of round ${round}`,
    ],
    log: 'Log',
    logEmpty: 'Brand places first, then the sides take turns.',
    moveLog: 'Move log',
    view: 'View',
    cameraView: 'Camera view',
    views: { brand: 'Brand side', rime: 'Rime side', top: 'Top' },
    animations: 'Animations',
    motion: { auto: 'Auto', on: 'On', off: 'Off' },
    reducedNote:
      'Your system asks apps to reduce motion, so warriors move without animating. Choose On to see them walk.',
    newLake: 'New lake',
    replay: 'Replay this lake',
    gloryResult: (glory: number, target: number) => `Glory: ${glory} of ${target}.`,
    changeBots: 'Change bots or sides',
  },

  board: {
    squareName: (name: string) => name,
    hole: 'hole',
    ice: (h: number) => `ice ${h}`,
    cracked: (opensHole: boolean, round: number) =>
      `cracked, ${opensHole ? 'opens a hole' : 'thins'} at the end of round ${round}`,
    runestone: (name: string) => name.toLowerCase(),
    walkHere: 'walk here',
    shoveHere: (strength: number, wall: number) => `shove ${strength} against ${wall}`,
    tone: {
      works: '',
      'sinks-enemy': ', sinks an enemy',
      'sinks-ally': ', sinks one of yours',
      fails: ', fails',
    },
    shoveFromHere: 'shove from here',
    sinkBadge: {
      off: 'off the ice',
      hole: 'into a hole',
      fall: '2-level fall',
      crush: 'crushed',
    } as Record<SinkReason, string>,
  },

  log: {
    offTheIce: 'off the ice',
    force: (force: number, shields: number) =>
      shields > 0 ? `${force} force + ${shields} shield` : `${force} force`,
    sums: (strength: number, wall: number, mine: string, theirs: string) =>
      `${strength} against ${wall} (${mine} against ${theirs})`,
    placed: (warrior: string, sq: string) => `${warrior} takes position on ${sq}.`,
    moved: (warrior: string, from: string, to: string) => `${warrior} moves ${from} to ${to}.`,
    shoved: (warrior: string, target: string, sq: string, sums: string) =>
      `${warrior} shoves ${target} on ${sq}: ${sums}.`,
    sunk: (warrior: string, reason: SinkReason) =>
      `${warrior} ${
        {
          off: 'is shoved off the ice',
          hole: 'falls into a hole',
          fall: 'falls from the ledge and breaks through',
          crush: 'is crushed against the ice',
        }[reason]
      }.`,
    passes: (side: string) => `${side} cannot move and passes.`,
    glory: (gained: number, total: number) => `Brand draws ${gained} Glory (${total} in all).`,
    givesWay: (sq: string, sunk: string | null) =>
      `The ice on ${sq} gives way${sunk ? `: ${sunk} goes under` : ''}.`,
    thins: (sq: string, height: number) => `The ice on ${sq} thins to ${height}.`,
    groans: (squares: string[]) => `The ice groans: ${squares.join(' and ')} crack.`,
    undone: (notation: string, next: string) => `Undone: ${notation}. ${next}.`,
    lakeStart: (seed: number) => `Lake #${seed}. Brand places a warrior first.`,
    lakeNow: (seed: number, next: string) => `Lake #${seed}. ${next}.`,
    shoveFailsNow: (strength: number, wall: number) =>
      `That shove fails: ${strength} against ${wall}. The shield wall holds.`,
    chooseFrom: 'Choose the square to shove from.',
  },

  outcome: {
    wins: (side: string) => `${side} wins`,
    glory: 'Brand drew enough power from the runestones.',
    held: 'Rime held the lake until the thaw.',
    sunk: (loser: string) => `Every ${loser} warrior went under.`,
    resign: (loser: string) => `${loser} resigned.`,
  },

  match: {
    you: 'You',
    host: 'The host',
    opponent: 'Your opponent',
    resigned: (who: string, defenderQuit: boolean, round: number) =>
      `${who} attacked; ${defenderQuit ? 'the defender' : 'the attacker'} resigned in round ${round}`,
    sankAll: (who: string, round: number) => `${who} sank every defender in round ${round}`,
    reached: (who: string, glory: number, round: number) =>
      `${who} reached ${glory} Glory in round ${round}`,
    lostAll: (who: string, round: number) => `${who} lost every warrior in round ${round}`,
    heldTo: (who: string, isYou: boolean, glory: number) =>
      `${who} ${isYou ? 'were' : 'was'} held to ${glory} Glory`,
    drawn: 'The match is drawn',
    youWin: 'You win the match',
    youLose: 'You lose the match',
    theyWin: (name: string) => `${name} wins the match`,
    theWinner: 'The winner',
    byResign: 'A player resigned.',
    byAttackOne: 'Only one attack took the runestones.',
    byAttackSingle: 'The game decided it.',
    fasterRounds: (fast: number, slow: number) =>
      `Both attacks succeeded; the faster one won (round ${fast} against round ${slow}).`,
    fasterSame: 'Both attacks succeeded in the same round; more Glory won.',
    moreGlory: (most: number, least: number) =>
      `Both attacks were held; more Glory won (${most} against ${least}).`,
    moreGlorySingle: 'More Glory won.',
    level: 'Nothing separated the two attacks.',
  },

  online: {
    title: 'Play a friend',
    intro:
      'Create a game, then send the link to your friend. Whoever opens it takes the other side.',
    format: 'Format',
    matchTitle: 'Match (recommended)',
    matchDetail: 'Two games on the same lake: each of you attacks once.',
    singleTitle: 'Single game',
    singleDetail: 'One game: one attacks, one defends.',
    attackFirst: 'You attack first?',
    yourSide: 'Your side',
    random: 'Random',
    randomDetail: 'Let the ice decide.',
    brandDetail: 'Attack: take the runestones.',
    rimeDetail: 'Defend until the thaw.',
    create: 'Create and get a link',
    outdatedTitle: 'This page is out of date',
    outdatedBody: 'The game server has been updated. Reload the page to keep playing.',
    reload: 'Reload',
    offline: 'Can’t reach the game server',
    connecting: 'Connecting…',
    tryingAgain: 'Trying again…',
    swap: (first: string, youAttack: boolean | null) =>
      `${first ? `${first}. ` : ''}Now the sides swap${youAttack === null ? '' : `: you ${youAttack ? 'attack' : 'defend'}`}.`,
    startGame2: 'Start game 2',
    newMatch: 'New match',
    waitingTitle: 'Waiting for your friend',
    waitingDetail: 'Send them this link. The game starts when they open it.',
    inviteLink: 'Invite link',
    copied: 'Copied',
    copy: 'Copy link',
    matchGame: (n: number) => `Match · game ${n} of 2`,
    singleGame: 'Single game',
    waitingName: 'Waiting…',
    youSuffix: ' (you)',
    away: 'away',
    youThisGame: (attack: boolean, side: string) =>
      `You ${attack ? 'attack' : 'defend'} this game (${side}).`,
    opponentAway: ' Your opponent is away.',
    watching: 'You are watching.',
    gameN: (n: number, text: string) => `Game ${n}: ${text}.`,
    reconnecting: 'Reconnecting…',
    confirmResign: (match: boolean) => `Confirm: resign${match ? ' the match' : ''}`,
    resign: 'Resign',
    nowWatching: (why: string) => `${why}: you are watching.`,
    errors: {
      GAME_OVER: 'The game is over',
      NOT_YOUR_TURN: 'It is not your turn',
      ILLEGAL_ACTION: 'That move is not allowed',
      GAME_NOT_FOUND: 'This match does not exist, or it has ended',
      NOT_A_PLAYER: 'You are not playing in this match',
      BAD_JOIN_TOKEN: 'This invite link is not valid',
      GAME_FULL: 'This invite has already been used',
      STALE_SEQ: 'The game moved on; catching up',
      RATE_LIMITED: 'Too many messages; slow down',
      WAITING_FOR_OPPONENT: 'Wait for your friend to join',
      CLIENT_OUTDATED: 'This page is out of date',
      INTERNAL: 'The game server had a problem',
    } as Partial<Record<ErrorCode, string>>,
  },

  botSetup: {
    title: 'Play the bot',
    you: 'You',
    brandTitle: 'Play Brand',
    brandDetail: 'You attack: take the runestones before the thaw.',
    rimeTitle: 'Play Rime',
    rimeDetail: 'You defend: hold the lake until round 16.',
    watchTitle: 'Watch two bots',
    watchDetail: 'Both sides are played by bots.',
    botOf: (side: string) => `${side} bot`,
    start: 'Start',
  },
};

export type Strings = typeof en;
